"use client";

import { useEffect, useState } from "react";
import { X, User, AtSign, Shield, Save, Eye, EyeOff, Lock, Pencil } from "lucide-react";
import Image from "next/image";
import authService, { Admin } from "../../../../services/authService";
import { useToast } from "../hooks/useToast";
import styles from "./ProfileDrawer.module.css";

interface ProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  defaultSection?: 'password';
}

export default function ProfileDrawer({ isOpen, onClose, defaultSection }: ProfileDrawerProps) {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [fullName, setFullName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingName, setEditingName] = useState(false);

  // Password states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      try {
        setLoading(true);
        const me = await authService.getCurrentAdmin();
        if (me) {
          setAdmin(me);
          setFullName(me.fullName);
        }
      } catch (e) {
        showError(e instanceof Error ? e.message : "Không thể tải hồ sơ");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen]);

  const handleSaveName = async () => {
    if (!fullName.trim() || fullName.trim().length < 3) {
      showError("Tên phải có ít nhất 3 ký tự");
      return;
    }
    try {
      setSaving(true);
      const res = await authService.updateProfile(fullName.trim());
      if (res.success && res.admin) {
        setAdmin(res.admin);
        showSuccess(res.message || "Đã cập nhật tên");
        setEditingName(false);
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : "Cập nhật thất bại");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      showError("Vui lòng nhập đủ mật khẩu");
      return;
    }
    if (newPassword.length < 8) {
      showError("Mật khẩu mới phải có ít nhất 8 ký tự");
      return;
    }
    // Kiểm tra mật khẩu phải có ít nhất 1 chữ hoa, 1 chữ thường và 1 ký tự đặc biệt
    if (!/[a-z]/.test(newPassword)) {
      showError("Mật khẩu mới phải chứa ít nhất 1 chữ thường");
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      showError("Mật khẩu mới phải chứa ít nhất 1 chữ hoa");
      return;
    }
    if (!/[@$!%*?&#]/.test(newPassword)) {
      showError("Mật khẩu mới phải chứa ít nhất 1 ký tự đặc biệt (@$!%*?&#)");
      return;
    }
    try {
      setSaving(true);
      const res = await authService.changePassword(currentPassword, newPassword);
      if (res.success) {
        showSuccess(res.message || "Đã đổi mật khẩu");
        setCurrentPassword("");
        setNewPassword("");
        setChangingPassword(false);
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : "Đổi mật khẩu thất bại");
    } finally {
      setSaving(false);
    }
  };

  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (isOpen && defaultSection === 'password') {
      setChangingPassword(true);
    } else if (!isOpen) {
      setChangingPassword(false);
    }
  }, [isOpen, defaultSection]);

  return (
    <div className={`${styles.overlay} ${isOpen ? styles.open : ''}`} aria-hidden={!isOpen}>
      <div className={styles.backdrop} onClick={onClose} />
      <aside className={styles.drawer} role="dialog" aria-modal="true">
        <div className={styles.header}>
          <h3>Hồ sơ cá nhân</h3>
          <button className={styles.iconBtn} onClick={onClose} aria-label="Đóng">
            <X size={18} />
          </button>
        </div>

        <div className={styles.profileCard}>
          <div className={styles.avatar}>
            <Image src="/assets/chat/default-avatar.svg" alt="Avatar" width={40} height={40} />
          </div>
          <div className={styles.info}>
            {!editingName ? (
              <div className={styles.textRow}>
                <div className={styles.nameText}>{fullName || admin?.fullName || ''}</div>
                <button className={styles.ghostBtn} onClick={() => setEditingName(true)} aria-label="Chỉnh sửa tên">
                  <Pencil size={16} />
                </button>
              </div>
            ) : (
              <div className={styles.nameRow}>
                <input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className={styles.input}
                  placeholder="Họ và tên"
                  maxLength={50}
                />
                <div className={styles.actionsRow}>
                  <button className={styles.primaryBtn} onClick={handleSaveName} disabled={saving || loading}>
                    <Save size={16} /> Lưu
                  </button>
                  <button className={styles.ghostBtn} onClick={() => { setFullName(admin?.fullName || ""); setEditingName(false); }}>Hủy</button>
                </div>
              </div>
            )}
            <div className={styles.meta}><AtSign size={14} /> <span>{admin?.email || ''}</span></div>
            <div className={styles.meta}><Shield size={14} /> <span>{admin?.role === 'admin' ? 'Quản trị viên' : 'Nhân viên'}</span></div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeaderRow}>
            <div className={styles.sectionTitle}><Lock size={16} /> Đổi mật khẩu</div>
            {!changingPassword && (
              <button className={styles.ghostBtn} onClick={() => setChangingPassword(true)}>Chỉnh sửa</button>
            )}
          </div>

          {changingPassword ? (
            <>
              <div className={styles.fieldRow}>
                <div className={styles.passwordWrap}>
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Mật khẩu hiện tại"
                    className={styles.input}
                  />
                  <button className={styles.eyeBtn} onClick={() => setShowCurrent(!showCurrent)}>
                    {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.passwordWrap}>
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mật khẩu mới (>= 8 ký tự, 1 hoa, 1 thường, 1 ký tự đặc biệt)"
                    className={styles.input}
                  />
                  <button className={styles.eyeBtn} onClick={() => setShowNew(!showNew)}>
                    {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className={styles.actionsRow}>
                <button className={styles.primaryBtn} onClick={handleChangePassword} disabled={saving || loading}>
                  <Lock size={16} /> Đổi mật khẩu
                </button>
                <button className={styles.ghostBtn} onClick={() => setChangingPassword(false)}>Hủy</button>
              </div>
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );
}


